import type CycleTimeChartController from 'components/CycleTimeChart/controller/CycleTimeChartController';
import { type RecordId } from 'javascripts/shared-types';
import moment from 'moment';
import ApplicationModel, { attr } from '../ApplicationModel';
import { CustomFieldDefinitionTypeEnum } from '../Enums';
import DateRangeFilter, {
  type DateRangeFilterValue,
} from '../filters/DateRangeFilter';
import type Filter from '../filters/Filter';
import NumericFilter, {
  type NumericFilterConfig,
} from '../filters/NumericFilter';
import RecordTypeFilter from '../filters/RecordTypeFilter';
import StatusRangeFilter, {
  type StatusRangeFilterValue,
} from '../filters/StatusRangeFilter';
import TagFilter from '../filters/TagFilter';
import TeamFilter from '../filters/TeamFilter';
import TimePeriodFilter, {
  type TimePeriodFilterValue,
} from '../filters/TimePeriodFilter';
import UserFilter from '../filters/UserFilter';
import ValueFilter from '../filters/ValueFilter';
import { loadCycleTimeEvents } from '../lib/CycleTime/loadEvents';
import { nameMap } from '../lib/nameMap';
import { model } from '../modelBuilder';
import { HasBookmarkFilters } from './concerns/HasBookmarkFilters';
import CustomFieldDefinition from './CustomFieldDefinition';
import { type BookmarksCycleTimeChartAttributes } from './generated/BookmarksCycleTimeChartAttributes';
import { type RecordEventRaw } from './generated/types';
import Project from './Project';
import Tag from './Tag';
import User from './User';
import Workflow from './Workflow';
import type WorkflowStatus from './WorkflowStatus';

export const DEFAULT_PERCENTILES = [50, 70, 85, 95];
export type Boundaries = { min?: number; max?: number };
export const SUPPORTED_CUSTOM_FIELD_TYPES = [
  CustomFieldDefinitionTypeEnum.NumberField.value,
  CustomFieldDefinitionTypeEnum.SelectConstant.value,
  CustomFieldDefinitionTypeEnum.SelectMultipleConstant.value,
] as const;

interface BookmarksCycleTimeChart
  extends BookmarksCycleTimeChartAttributes,
    HasBookmarkFilters<
      {
        recordType: RecordTypeFilter;
        users: UserFilter;
        timePeriod: TimePeriodFilter;
        dateRange: DateRangeFilter;
        statusRange: StatusRangeFilter;
        tags: TagFilter;
        featureType: ValueFilter<string | undefined>;
        originalEstimate: NumericFilter;
        customFields: ValueFilter<string[] | undefined>;
        teams: TeamFilter;

        // Chart configuration - not true filters but easier to manage as Filter objects
        boundaries: ValueFilter<Boundaries>;
        target: ValueFilter<number | undefined>;
        percentiles: ValueFilter<number[]>;
      } & Record<
        string,
        ValueFilter<string | string[] | NumericFilterConfig | undefined>
      > // Custom field filters are created dynamically
    > {
  filters: {
    recordType?: string[];
    users?: RecordId[];
    tags?: RecordId[];
    percentiles?: number[];
    dateRange: DateRangeFilterValue;
    featureType?: string;
    timePeriod: TimePeriodFilterValue;
    statusRange?: StatusRangeFilterValue;
    boundaries?: Boundaries;
    target?: number;
    customFields?: string[];
    originalEstimate?: NumericFilterConfig;
    teams?: RecordId[];
  };
}

@model
@HasBookmarkFilters
class BookmarksCycleTimeChart extends ApplicationModel {
  // Start auto-generated
  static typename: 'BookmarksCycleTimeChart' = 'BookmarksCycleTimeChart';
  get typename(): 'BookmarksCycleTimeChart' {
    return 'BookmarksCycleTimeChart';
  }

  static internalTypename: 'Bookmarks::CycleTimeChart' =
    'Bookmarks::CycleTimeChart';
  get internalTypename(): 'Bookmarks::CycleTimeChart' {
    return 'Bookmarks::CycleTimeChart';
  }

  static bookmarkName = 'Bookmarks::CycleTimeChart';

  static inputType = 'CycleTimeChartAttributes!';

  static fields = {
    filters: attr({ writable: true, readable: true, json: true }),
    id: attr({ readable: true }),
    projectId: attr({ readable: true }),
    project: attr.belongsTo({ readable: true, typename: 'Project' }),
  };
  // End auto-generated

  /**
   * Initialize the filters
   */
  async createFilters() {
    const [users, tags, statusRange, { customFields }] = await Promise.all([
      this.availableUsers(),
      this.selectedTags(),
      this.getStatusFilter(),
      this.preloadFilterOptions(),
    ]);

    let allowedRecordTypes: ('Epic' | 'Feature' | 'Requirement')[] = [
      'Epic',
      'Feature',
      'Requirement',
    ];

    let initialRecordTypes =
      typeof this.filters?.recordType === 'string'
        ? [this.filters.recordType]
        : this.filters.recordType ?? [];

    if (this.project && !this.project.epicsEnabled) {
      initialRecordTypes = initialRecordTypes.filter(t => t !== 'Epic');
      allowedRecordTypes = allowedRecordTypes.filter(t => t !== 'Epic');
    }

    const filters: Record<string, Filter> = {
      recordType: new RecordTypeFilter({
        initialItems: initialRecordTypes,
        recordTypes: allowedRecordTypes,
      }),
      users: new UserFilter(this.filters.users || [], users.map(nameMap)),
      statusRange,
      tags: new TagFilter(this.filters?.tags ?? [], tags),
    };

    if (this.isMultilineReport) {
      const allTeams: [string, RecordId][] =
        this.project?.children.map(p => [p.name, p.id]) ?? [];
      filters.teams = new TeamFilter(this.filters.teams || [], allTeams);
    } else {
      filters.teams = new TeamFilter([], []);
    }

    filters.boundaries = new ValueFilter(this.filters.boundaries || {});
    filters.target = new ValueFilter(this.filters.target);
    filters.percentiles = new ValueFilter(
      this.filters.percentiles || DEFAULT_PERCENTILES
    );

    filters.featureType = new ValueFilter(this.filters.featureType);
    filters.customFields = new ValueFilter(this.filters.customFields);

    if (!this.filters.dateRange && this.filters.timePeriod) {
      // First load of existing chart after rework - set the date range from the time period filter
      const existingFilter = new TimePeriodFilter(this.filters.timePeriod);

      filters.dateRange = existingFilter.asDateRangeFilter();
    } else {
      filters.dateRange = new DateRangeFilter(this.filters.dateRange, false);
    }

    for (const customField of customFields) {
      filters[customField.key] = new ValueFilter(this.filters[customField.key]);
    }

    filters.originalEstimate = new NumericFilter(this.filters.originalEstimate);

    return filters;
  }

  async getStatusFilter() {
    const { workflowStatuses: statuses } = await this.preloadFilterOptions();

    let statusFilter;

    // Check the filtered statuses exist. If not, try to set to cycle time
    // then lead time. Failing that, set to custom using the first and last
    // status available.
    if (
      this.filters.statusRange?.type &&
      statuses.find(({ id }) => id === this.filters.statusRange?.start) &&
      statuses.find(({ id }) => id === this.filters.statusRange?.end)
    ) {
      statusFilter = new StatusRangeFilter(this.filters.statusRange);
    } else {
      const doneStatuses = statuses.filter(status => status.isDelivered);
      const notStarted = statuses.find(status => !status.isStarted)?.id;
      const inProgress = statuses.find(status => status.isInProgress)?.id;
      const done = doneStatuses[doneStatuses.length - 1]?.id;

      if (inProgress && done) {
        statusFilter = new StatusRangeFilter({
          type: 'CYCLE_TIME',
          start: inProgress,
          end: done,
        });
      } else if (notStarted && done) {
        statusFilter = new StatusRangeFilter({
          type: 'LEAD_TIME',
          start: inProgress,
          end: done,
        });
      } else {
        statusFilter = new StatusRangeFilter({
          type: 'CUSTOM',
          start: statuses[0]?.id,
          end: statuses[statuses.length - 1]?.id,
        });
      }
    }

    return statusFilter;
  }

  canFilter(scope) {
    if (scope === 'teams') {
      return this.isMultilineReport;
    }
    return true;
  }

  async selectedTags(): Promise<[string, RecordId][]> {
    const tagIds = this.filters.tags || [];

    if (tagIds.length > 0) {
      const records = await Tag.select(['id', 'name'])
        .where({
          id: tagIds,
        })
        .all();
      return records.map(t => [t.name, t.id || ('' as RecordId)]);
    }

    return [];
  }

  /**
   * Load all the users available to this team
   */
  availableUsers(): Promise<User[]> {
    if (!this.local.users) {
      this.local.users = User.select('id', 'name', 'initials', 'email')
        .select({ avatarUrl: { size: aha.enums.AvatarSizeEnum.SIZE_40 } })
        .where({ projectId: this.projectId })
        .per(100)
        .all();
    }

    return this.local.users;
  }

  preloadFilterOptions(): Promise<{
    workflowStatuses: WorkflowStatus[];
    customFields: CustomFieldDefinition[];
  }> {
    if (!this.local.optionData) {
      this.local.optionData = new Promise<{
        workflowStatuses: WorkflowStatus[];
        customFields: CustomFieldDefinition[];
      }>(resolve => {
        void Project.select('id')
          .merge({
            children: Project.select('id').merge({
              featureWorkflow: Workflow.select('id').merge({
                workflowStatuses: [
                  'id',
                  'name',
                  'position',
                  'color',
                  'internalMeaning',
                ],
              }),
            }),
            featureWorkflow: Workflow.select('id').merge({
              workflowStatuses: [
                'id',
                'name',
                'position',
                'color',
                'internalMeaning',
              ],
            }),
          })
          .merge({
            customFieldsRelatedToTeam: CustomFieldDefinition.select([
              'id',
              'name',
              'key',
              'type',
            ])
              .merge({
                customFieldOptions: ['id', 'name'],
              })
              .argument({
                types: SUPPORTED_CUSTOM_FIELD_TYPES,
              }),
          })
          .find(this.projectId)
          .then(project => {
            if (!project) {
              return {
                workflowStatuses: [],
                customFields: [],
              };
            } else if (this.isMultilineReport) {
              return {
                workflowStatuses:
                  project.children[0]?.featureWorkflow.workflowStatuses ?? [], // TODO: To be improved after demo 13 Nov 2025, assumes all children have equivalent workflows
                customFields: project?.customFieldsRelatedToTeam ?? [],
              };
            } else {
              return {
                workflowStatuses:
                  project.featureWorkflow.workflowStatuses ?? [],
                customFields: project?.customFieldsRelatedToTeam ?? [],
              };
            }
          })
          .then(resolve);
      });
    }
    return this.local.optionData;
  }

  /**
   * Load the selected user
   */
  async user(): Promise<User | undefined> {
    const userId = (this.getFilter('users') as UserFilter).single();
    if (!userId) return undefined;
    const users = await this.availableUsers();
    return users.find(u => u.id === userId);
  }

  /**
   * Updates the list of all possible filter IDs with `options`. For
   * example, will tell the `UserFilter` about all users once the
   * Selectro is first opened.
   */
  setFilterOptions(scope, options) {
    (this.getFilter(scope) as UserFilter | undefined)?.setOptions(options);
  }

  async recordEvents(
    controller: CycleTimeChartController
  ): Promise<RecordEventRaw[]> {
    const dateRange = this.getFilter('dateRange');

    if (!dateRange) return new Promise(() => []);

    const startDate = dateRange.startTime.toISOString();
    const endDate = dateRange.endTime.toISOString();

    const teamFilter = this.getFilter('teams');
    const teamIds = teamFilter
      ? teamFilter.selectroItems().map(item => item.value)
      : undefined;

    const { workflowStatuses, customFields } =
      await this.preloadFilterOptions();

    const data = await loadCycleTimeEvents({
      startDate,
      endDate,
      availableStatuses: workflowStatuses,
      customFieldKeys: customFields.map(f => f.key),
      projectId: this.projectId,
      teamIds: teamIds,
      controller,
    });

    return data
      .map(event => ({
        ...event,
        createdAtTimestamp: moment(event.createdAt).valueOf(),
      }))
      .sort((a, b) => {
        const aTime = a.createdAtTimestamp;
        const bTime = b.createdAtTimestamp;
        if (aTime === bTime) {
          return a.teamWorkflowStatus.position - b.teamWorkflowStatus.position;
        } else {
          return aTime - bTime;
        }
      });
  }

  get isMultilineReport(): boolean {
    return (
      window.featureFlags?.develop_1927_team_line_report_updates &&
      !!this.project?.isProductLine
    );
  }
}

export default BookmarksCycleTimeChart;
